import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Smartphone } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function AuthModal() {
  const { authModalOpen, closeAuth, refresh, setUser } = useAuth();
  const [mode, setMode] = useState("choose"); // choose | phone | otp
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!authModalOpen) {
      setMode("choose"); setPhone(""); setOtp(""); setDevOtp(null); setLoading(false);
    }
  }, [authModalOpen]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    if (!phone.trim()) { toast.error("Enter your mobile number."); return; }
    setLoading(true);
    try {
      const res = await api.post("/auth/phone/send-otp", { phone: phone.trim() });
      setDevOtp(res.data.dev_otp || null);
      setMode("otp");
      toast.success("OTP sent." + (res.data.dev_otp ? " (dev mode)" : ""));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send OTP.");
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    if (otp.trim().length !== 6) { toast.error("Enter the 6-digit OTP."); return; }
    setLoading(true);
    try {
      const res = await api.post("/auth/phone/verify-otp", { phone: phone.trim(), otp: otp.trim() });
      setUser(res.data);
      await refresh();
      toast.success("Welcome to the maison.");
      closeAuth();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid OTP.");
    } finally { setLoading(false); }
  };

  return (
    <AnimatePresence>
      {authModalOpen && (
        <motion.div
          data-testid="auth-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeAuth}
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative bg-midnight-deep border border-white/10 w-full max-w-md p-8 md:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeAuth}
              aria-label="Close"
              data-testid="auth-modal-close"
              className="absolute top-4 right-4 text-white/60 hover:text-white"
            >
              <X size={20} strokeWidth={1.3} />
            </button>

            <div className="eyebrow mb-4 text-silver/80">Maison Access</div>
            <h2 className="font-serif text-white text-3xl md:text-4xl leading-tight">
              Enter the <span className="italic text-silver">circle</span>.
            </h2>
            <p className="mt-3 text-white/55 text-sm font-light">
              Sign in to save your edits, track orders, and access private dispatches.
            </p>

            {mode === "choose" && (
              <div className="mt-8 space-y-3">
                <button
                  onClick={handleGoogle}
                  data-testid="auth-google-btn"
                  className="w-full flex items-center justify-center gap-3 bg-white text-black py-3.5 uppercase tracking-[0.28em] text-[11px] hover:bg-silver transition-colors"
                >
                  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
                    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.9 6.3 29.2 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c10.8 0 19.5-8.7 19.5-19.5 0-1.3-.1-2.3-.4-3.5z"/>
                    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.9 6.3 29.2 4.5 24 4.5 16.3 4.5 9.6 8.7 6.3 14.7z"/>
                    <path fill="#4CAF50" d="M24 43.5c5.1 0 9.8-1.9 13.4-5l-6.2-5.1c-1.9 1.5-4.4 2.4-7.2 2.4-5.2 0-9.6-3.1-11.3-7.5L6.1 33C9.4 39 16.2 43.5 24 43.5z"/>
                    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.3 4.1-4.1 5.4l6.2 5.1c-.4.4 6.6-4.8 6.6-14.5 0-1.3-.1-2.3-.4-3.5z"/>
                  </svg>
                  Continue with Google
                </button>

                <div className="flex items-center gap-4 my-4">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-[10px] tracking-[0.4em] uppercase text-white/40">or</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>

                <button
                  onClick={() => setMode("phone")}
                  data-testid="auth-phone-btn"
                  className="w-full flex items-center justify-center gap-3 border border-white/20 text-white py-3.5 uppercase tracking-[0.28em] text-[11px] hover:border-white hover:bg-white/5 transition-colors"
                >
                  <Smartphone size={15} strokeWidth={1.3} />
                  Continue with Mobile
                </button>
              </div>
            )}

            {mode === "phone" && (
              <form onSubmit={handleSendOtp} className="mt-8 space-y-6">
                <div>
                  <label className="text-[10px] tracking-[0.35em] uppercase text-silver/70">Mobile Number</label>
                  <input
                    data-testid="auth-phone-input"
                    type="tel"
                    autoFocus
                    className="lux-input"
                    placeholder="+91 98765 43210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                  <p className="mt-2 text-[10px] text-white/40 tracking-[0.2em] uppercase">India default · International with country code</p>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="auth-send-otp"
                  className="w-full flex items-center justify-center gap-3 bg-white text-black py-3.5 uppercase tracking-[0.28em] text-[11px] hover:bg-silver disabled:opacity-50 transition-colors"
                >
                  {loading ? "Sending…" : <>Send OTP <ArrowRight size={14} strokeWidth={1.4} /></>}
                </button>
                <button type="button" onClick={() => setMode("choose")} className="text-[10px] tracking-[0.32em] uppercase text-white/50 hover:text-white">
                  ← Back
                </button>
              </form>
            )}

            {mode === "otp" && (
              <form onSubmit={handleVerifyOtp} className="mt-8 space-y-6">
                <div>
                  <label className="text-[10px] tracking-[0.35em] uppercase text-silver/70">6-Digit OTP</label>
                  <input
                    data-testid="auth-otp-input"
                    type="text"
                    inputMode="numeric"
                    autoFocus
                    maxLength={6}
                    className="lux-input tracking-[0.8em] text-2xl"
                    placeholder="••••••"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  />
                  {devOtp && (
                    <p data-testid="auth-dev-otp" className="mt-3 text-[10px] tracking-[0.28em] uppercase text-silver">
                      Dev mode OTP: <span className="text-white">{devOtp}</span>
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  data-testid="auth-verify-otp"
                  className="w-full flex items-center justify-center gap-3 bg-white text-black py-3.5 uppercase tracking-[0.28em] text-[11px] hover:bg-silver disabled:opacity-50 transition-colors"
                >
                  {loading ? "Verifying…" : "Verify & Continue"}
                </button>
                <button
                  type="button"
                  onClick={() => { setMode("phone"); setOtp(""); setDevOtp(null); }}
                  className="text-[10px] tracking-[0.32em] uppercase text-white/50 hover:text-white"
                >
                  ← Change number
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
